// src/shared/components/PreAuthCard.jsx
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ProgressStepDots from './ProgressStepDots.jsx';

/**
 * PreAuthCard
 * - Generic card for authentication flows (login, signup, password reset)
 * - Props:
 *   - children: card content
 *   - className: extra classes
 *   - style: optional style overrides
 *   - header: optional { title, stepLabel, icon, variant }
 *     - title: e.g. "1 Create Account"
 *     - stepLabel: e.g. "Step 1 of 3"
 *     - icon: Bootstrap icon class (e.g. "bi-person-plus-fill")
 *     - variant: 'primary' | 'success' | 'purple' for bar and dots
 */
const HEADER_BG = {
  primary: 'bg-primary',
  success: 'bg-success',
  purple: '',
};

export default function PreAuthCard({ children, className, style, header }) {
  const headerVariant = header?.variant || 'primary';
  const headerBgStyle =
    headerVariant === 'purple'
      ? { background: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)' }
      : undefined;

  return (
    <div
      className={classNames(
        'card shadow-sm border-0 rounded-4 overflow-hidden',
        !header && 'p-4',
        className
      )}
      style={style}
    >
      {header && (
        <div
          className={classNames(
            'p-3 text-white rounded-top-4',
            !headerBgStyle && (HEADER_BG[headerVariant] || 'bg-primary')
          )}
          style={headerBgStyle}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h2 className="h6 mb-0 fw-bold text-white">{header.title}</h2>
              {header.stepLabel && (
                <span className="small opacity-90">{header.stepLabel}</span>
              )}
            </div>
            {header.icon && (
              <i className={classNames('bi', header.icon, 'fs-4 opacity-90')} aria-hidden />
            )}
          </div>
          {header.completed != null && header.total != null && (
            <div className="mt-2">
              <ProgressStepDots
                completed={header.completed}
                total={header.total}
                variant={headerVariant === 'purple' ? 'purple' : headerVariant === 'success' ? 'green' : 'primary'}
                size="sm"
              />
            </div>
          )}
        </div>
      )}
      <div className={header ? 'p-4' : undefined}>{children}</div>
    </div>
  );
}

PreAuthCard.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object,
  header: PropTypes.shape({
    title: PropTypes.string,
    stepLabel: PropTypes.string,
    icon: PropTypes.string,
    variant: PropTypes.oneOf(['primary', 'success', 'purple']),
    completed: PropTypes.number,
    total: PropTypes.number,
  }),
};

PreAuthCard.defaultProps = {
  className: '',
  style: {},
  header: null,
};
